import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Textarea } from 'components/Form/Input'
import { AsyncMultiselect } from 'components/Select/AsyncMultiselect'
import { Option } from 'components/Select/Select'
import { CalendarInput } from 'components/CalendarInput/CalendarInput'
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from 'components/ModalV2/Modal'
import { Button, Flex } from 'styling/GlobalStyles'
import { Heading2, NormalText } from 'styling/TypographyV2'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { assignUsersToTeam } from 'features/operationalManagement/operationalManagementSlice'
import { IBasicUserSimple } from 'features/operationalManagement/operationalManagement.types'
import { baseUrl } from 'utils/axios'
import { ModalLabel, ModalRow } from '../Organogram.styled'

interface AssignEmployeeModalProps {
  teamId: number
  teamName: string
  onClose: () => void
  onAssigned: () => void
}

export const AssignEmployeeModal = ({
  teamId,
  teamName,
  onClose,
  onAssigned,
}: AssignEmployeeModalProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const assigningEmployees = useSelector(
    (state: RootState) => state.operationalManagement.assigningEmployees,
  )

  const [userValues, setUserValues] = useState<Option[]>([])
  const [effectiveDate, setEffectiveDate] = useState<Date | null>(new Date())
  const [reason, setReason] = useState('')

  const userIds = userValues.map((v) => v.value as number)
  const trimmedReason = reason.trim()
  const canSubmit =
    userIds.length > 0 && effectiveDate != null && trimmedReason.length > 0 && !assigningEmployees

  const handleSave = async () => {
    if (!canSubmit || !effectiveDate) return
    const result = await dispatch(
      assignUsersToTeam({
        userIds,
        operationalTeamId: teamId,
        startDate: effectiveDate.toISOString(),
        reason: trimmedReason,
      }),
    )
    if (assignUsersToTeam.fulfilled.match(result)) {
      onAssigned()
      onClose()
    }
  }

  return (
    <Modal onClose={onClose}>
      <ModalContent maxWidth='520px'>
        <ModalHeader>
          <Flex between center>
            <Heading2>Assign Employee to {teamName}</Heading2>
            <CloseIcon onClick={onClose} />
          </Flex>
        </ModalHeader>
        <ModalBody>
          <NormalText mb='1.25rem'>Add an employee to this team&apos;s active roster.</NormalText>

          <ModalRow>
            <ModalLabel>Employees *</ModalLabel>
            <AsyncMultiselect<IBasicUserSimple>
              queryKey='assign-employee-users'
              placeholder='Select employees'
              values={userValues}
              loadOptions={async (q) => {
                const resp = await baseUrl.get<{ data: IBasicUserSimple[] }>('users/basic', {
                  params: { page: 1, limit: 20, state: 1, ...(q ? { q } : {}) },
                })
                return resp.data.data
              }}
              decoder={(u) => ({ label: u.fullName, value: u.id })}
              onChange={(vals) => setUserValues(vals ?? [])}
            />
          </ModalRow>

          <ModalRow>
            <ModalLabel>Effective date *</ModalLabel>
            <CalendarInput
              width='100%'
              defaultValue={effectiveDate}
              onChange={(d) => setEffectiveDate(d instanceof Date ? d : null)}
            />
          </ModalRow>

          <ModalRow>
            <Textarea
              label='Reason for Assignment *'
              placeholder='e.g. New hire onboarding, transfer from Bravo Crew...'
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
            />
          </ModalRow>
        </ModalBody>
        <ModalFooter>
          <Button white onClick={onClose} disabled={assigningEmployees}>
            Cancel
          </Button>
          <Button orange onClick={handleSave} disabled={!canSubmit}>
            {assigningEmployees ? 'Assigning…' : '+ Assign Employee'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
